// cart-progress-bar.js — tiered progress bar (unified via PBCart)
(function () {
  const drawer = document.getElementById('UnifiedCartDrawer');
  if (!drawer) return;

  const barHost = document.getElementById('CartProgressBar');
  const msgEl   = document.getElementById('CartProgressMessage');
  if (!barHost) return;

  // --- Excluded product IDs (gift cards + memberships) ---
  // Lines with these product IDs are excluded from the tier subtotal
  const EXCLUDED_PRODUCT_IDS = [
    'gid://shopify/Product/4849416896586',  // Gift card
    'gid://shopify/Product/15652866294134', // Membership
    'gid://shopify/Product/15630492238198'  // Membership
  ];

  /**
   * Returns the cart subtotal in cents, excluding gift card lines.
   * Works with the Storefront API GraphQL cart object (lines.edges[].node).
   */
  function subtotalExcludingGiftCards(gqlCart) {
    const edges = gqlCart?.lines?.edges || [];

    if (edges.length === 0) {
      // Fallback: if lines are missing (e.g. cart not yet hydrated),
      // fall back to the full subtotal so the bar still renders.
      return Math.round(parseFloat(gqlCart?.cost?.subtotalAmount?.amount || 0) * 100);
    }

    const totalEuros = edges.reduce((sum, { node: line }) => {
      const productId = line.merchandise?.product?.id || '';
      if (EXCLUDED_PRODUCT_IDS.includes(productId)) return sum;
      return sum + parseFloat(line.cost?.totalAmount?.amount || 0);
    }, 0);

    return Math.round(totalEuros * 100);
  }

  // --- Tiers ---
  function readTiers() {
    const nodes = drawer.querySelectorAll('[data-tier]');
    const tiers = [...nodes].map(n => ({
      id: n.dataset.id,
      threshold: Number(n.dataset.threshold || 0),
      rewardType: n.dataset.rewardType || 'free_product',
      productTitle: n.dataset.productTitle || '',
      productLabel: n.dataset.productLabel || '',
      useRemaining: n.dataset.useRemaining === 'true',
      useReached:   n.dataset.useReached === 'true',
      remainingTpl: n.dataset.remainingLabel || 'Nog {remaining_amount} om te genieten van je {product_title}!',
      reachedTpl:   n.dataset.reachedLabel   || 'Je hebt een {product_title} ontgrendeld.',
      stepLabel: n.dataset.stepLabel || '',
      stepIcon:  n.dataset.stepIcon  || ''
    }))
    .filter(t => !Number.isNaN(t.threshold))
    .sort((a, b) => a.threshold - b.threshold);

    return tiers.slice(0, 5);
  }

  function eur(cents) {
    const v = Math.max(0, Number(cents) || 0) / 100;
    try { return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v); }
    catch { return '€' + v.toFixed(2).replace('.', ','); }
  }

  function buildBar(tiers) {
    // Guard based on sorted tier thresholds — rebuilds if tiers were unsorted on first call
    const key = tiers.map(t => t.threshold).join(',');
    if (barHost.dataset.built === key) return;
    barHost.dataset.built = key;

    const steps = tiers.map(t => {
      const icon = t.stepIcon
        ? `<img class="icon" src="${t.stepIcon}" alt="">`
        : '';
      const price = t.threshold > 0 ? eur(t.threshold * 100) : '';

      return `
        <div class="step" data-th="${t.threshold}" data-pp="${price}">
          <div class="dot">${icon}</div>
          <div class="label">${t.stepLabel || ''}</div>
        </div>
      `;
    }).join('');

    barHost.innerHTML = `
      <div class="progress-bar__wrapper">
        <div class="progress-bar__track">
          <div class="progress-bar__fill" style="width:0"></div>
        </div>
        <div class="progress-bar__steps">
          ${steps}
        </div>
      </div>
    `;
  }

  function setMessage(tier, cartTotalEuros, reached) {
    if (!msgEl) return;
    const remainingCents = Math.max(0, (tier.threshold - cartTotalEuros) * 100);
    const productTitle = tier.productTitle || tier.productLabel || '';

    const apply = (tpl) =>
      (tpl || '')
        .replace(/\{remaining_amount\}/g, eur(remainingCents))
        .replace(/\{product_title\}/g, productTitle);

    const defaults = {
      remaining: {
        free_shipping:   'Nog {remaining_amount} tot gratis verzending!',
        free_product:    'Nog {remaining_amount} om te genieten van je {product_title}!',
        discount_percent:'Nog {remaining_amount} tot korting!'
      },
      reached: {
        free_shipping:   'Gratis verzending ontgrendeld.',
        free_product:    'Je hebt een {product_title} ontgrendeld.',
        discount_percent:'Korting ontgrendeld.'
      }
    };

    let tpl = '';
    if (reached) {
      tpl = tier.useReached ? tier.reachedTpl : '';
      if (!tpl) tpl = defaults.reached[tier.rewardType] || '';
    } else {
      tpl = tier.useRemaining ? tier.remainingTpl : '';
      if (!tpl) tpl = defaults.remaining[tier.rewardType] || '';
    }

    msgEl.textContent = apply(tpl);
  }

  // --- Main update ---
  function update(cartObj) {
    const tiers = readTiers();
    if (!tiers.length) return;

    buildBar(tiers);

    const fill    = barHost.querySelector('.progress-bar__fill');
    const stepEls = [...barHost.querySelectorAll('.progress-bar__steps .step')];

    const euros = (cartObj.total_price || 0) / 100;

    stepEls.forEach(el => {
      const th = Number(el.dataset.th || 0);
      el.classList.toggle('step--complete', euros >= th);
    });

    let fillPercent = 0;
    const segmentCount = tiers.length - 1;
    const segmentWidth = 100 / segmentCount;

    for (let i = 0; i < segmentCount; i++) {
      const segmentStart = tiers[i].threshold;
      const segmentEnd   = tiers[i + 1].threshold;
      const segmentRange = segmentEnd - segmentStart;

      if (euros >= segmentEnd) {
        fillPercent += segmentWidth;
      } else if (euros >= segmentStart) {
        const progress = euros;
        const fillInSegment = ((progress - segmentStart) / segmentRange) * segmentWidth;
        fillPercent += fillInSegment;
        break;
      } else if (euros < segmentStart && i === 0) {
        const fillInSegment = (euros / segmentStart) * segmentWidth;
        fillPercent += fillInSegment;
        break;
      } else {
        break;
      }
    }

    fillPercent = Math.min(100, Math.max(0, fillPercent));
    if (fill) fill.style.width = fillPercent + '%';

    let active = tiers.find(t => euros < t.threshold);
    if (!active) {
      active = tiers[tiers.length - 1];
      setMessage(active, euros, true);
    } else {
      setMessage(active, euros, false);
    }
  }

  // --- Hooks ---
  async function refresh() {
    try {
      const gqlCart = await PBCart.refresh();
      const cart = {
        total_price: subtotalExcludingGiftCards(gqlCart)
      };
      update(cart);
    } catch (e) {
      // ignore errors
    }
  }

  

  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('pb:cart:updated', e => {
    const gqlCart = e.detail?.cart;
    if (gqlCart) {
      const cart = {
        total_price: subtotalExcludingGiftCards(gqlCart)
      };
      update(cart);
    }
  });
  document.addEventListener('cart:open', refresh);
})();

// ---------- WONDR 5 JAAR — Prize draw teaser ----------
(function () {
  const PRIZE_MIN_QTY = 4;
  const WONDR_PRIZE_ATTRIBUTE = 'wondr_prize_number';

  function updatePrizeTeaser(cart) {
    // Alleen tonen als campagne actief is
    const isTestMode = new URLSearchParams(location.search).get('wondr_test') === '1';
    const isCampaignLive = false; // ← zelfde flag als in pb-cart.js
    if (!isTestMode && !isCampaignLive) {
      document.getElementById('WondrPrizeTeaser')?.remove();
      return;
    }

    const msgEl = document.getElementById('CartProgressMessage');
    if (!msgEl) return;

    const lines = (cart?.lines?.edges || []).map(e => e.node);

    // Eligible quantity (excl. free gifts)
    const eligibleQty = lines
      .filter(line => !line.attributes?.some(a => a.key === '_auto_gift' && a.value === 'true'))
      .reduce((sum, line) => sum + line.quantity, 0);

    // Al een prize getrokken? Dan niets tonen
    const alreadyDrawn = cart?.attributes?.find(a => a.key === WONDR_PRIZE_ATTRIBUTE)?.value;
    if (alreadyDrawn) return;

    const remaining = PRIZE_MIN_QTY - eligibleQty;

    if (remaining > 0) {
      // Aparte div zodat we de bestaande progress message niet overschrijven
      let teaserEl = document.getElementById('WondrPrizeTeaser');
      if (!teaserEl) {
        teaserEl = document.createElement('p');
        teaserEl.id = 'WondrPrizeTeaser';
        teaserEl.className = 'progress-message';
        teaserEl.style.cssText = 'margin-top:4px;';
        msgEl.parentNode.insertBefore(teaserEl, msgEl.nextSibling);
      }
      teaserEl.textContent = `🎁 Nog ${remaining} product${remaining > 1 ? 'en' : ''} om te mogen draaien aan het rad na betaling!`;
    } else {
      // Drempel bereikt maar nog niet naar checkout gegaan
      let teaserEl = document.getElementById('WondrPrizeTeaser');
      if (!teaserEl) {
        teaserEl = document.createElement('p');
        teaserEl.id = 'WondrPrizeTeaser';
        teaserEl.className = 'progress-message';
        teaserEl.style.cssText = 'margin-top:4px;';
        msgEl.parentNode.insertBefore(teaserEl, msgEl.nextSibling);
      }
      teaserEl.textContent = `🎉 Voltooi je bestelling om aan het rad te draaien!`;
    }
  }

  document.addEventListener('pb:cart:updated', (e) => {
    updatePrizeTeaser(e.detail?.cart);
  });
})();